/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { Breadcrumb as BreadcrumbType } from '@toucan/common/services/taxonomy/config/types';

import BackIcon from '@toucan/client/global/icons/chevron-left.svg';

import { MediaQuery, useMediaQuery } from '@toucan/client/global/application';
import { WrappedLink } from '@toucan/client/global/nextjs';

import {
    LinkProps,
    LinkSize,
    LinkVariant,
} from '@toucan/client/shared/components/link';
import {
    LinkList,
    LinkListSeparator,
} from '@toucan/client/shared/components/link-list';

import { styles } from './breadcrumb.styles';

/**
 * NOTE: The variant refers to the background that this component sits on
 * e.g LIGHT means the component sits on a light coloured background
 */
export enum BreadcrumbVariant {
    LIGHT = 'light',
    DARK = 'dark',
}

export type BreadcrumbProps = {
    /**
     * An array of links to be displayed in the breadcrumb.
     */
    links: BreadcrumbType[];

    /**
     * The variant of the Breadcrumb component to display on desktop
     * 1. LIGHT
     * 2. DARK
     *
     * NOTE: The variant refers to the background that this component sits on
     * e.g LIGHT means the component sits on a light coloured background
     *
     * @default LIGHT
     */
    desktopVariant?: BreadcrumbVariant;

    /**
     * The variant of the Breadcrumb component to display on mobile
     * 1. LIGHT
     * 2. DARK
     *
     * NOTE: The variant refers to the background that this component sits on
     * e.g LIGHT means the component sits on a light coloured background
     *
     * @default LIGHT
     */
    mobileVariant?: BreadcrumbVariant;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
};

export type StyledBreadcrumbProps = Pick<
    BreadcrumbProps,
    'desktopVariant' | 'mobileVariant' | 'data-testid' | 'className'
>;

export const StyledBreadcrumb = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const Breadcrumb: FunctionComponent<BreadcrumbProps> = ({
    mobileVariant = BreadcrumbVariant.LIGHT,
    desktopVariant = BreadcrumbVariant.LIGHT,
    links,
    ...otherProps
}: BreadcrumbProps) => {
    const { t } = useTranslation();
    const isTablet = useMediaQuery(MediaQuery.LARGE_TABLET);

    /**
     * Get the mobile back button href.
     *
     * @param links the list of breadcrumb links
     */
    function getBackHref(links: BreadcrumbType[]) {
        return links.length > 1 ? links[links.length - 2].href : '/';
    }

    /**
     * Get the mobile back button text
     * @param links the list of breadcrumb links
     */
    function getBackText(links: BreadcrumbType[]) {
        let text =
            links.length > 1 ? links[links.length - 2].children : undefined;

        if (text === undefined || typeof text !== 'string') {
            text = getBackHref(links) === '/' ? t('Home') : t('Back');
        }

        return text as string;
    }

    /**
     * Get the next template path for the back button
     *
     * @param links the list of breadcrumb links
     */
    function getBackTemplatePath(links: BreadcrumbType[]) {
        let templatePath =
            links.length > 1 ? links[links.length - 2].templatePath : undefined;

        if (templatePath === undefined) {
            // default to homepage template
            templatePath = '/index';
        }

        return templatePath;
    }

    /**
     * Get the truncated list of links for use on tablets.
     *
     * The truncated version of the breadcrumb consists of:
     * Home > … > (previous page) > (current page)
     *
     * @param links the list of breadcrumb links
     */
    function truncateLinks(links: BreadcrumbType[]): BreadcrumbType[] {
        if (links.length < 3) {
            return links;
        }
        const currentPage = links[links.length - 1];
        const parentPage = links[links.length - 2];
        const truncated = {
            ...links[links.length - 3],
            children: '...',
        };
        return [truncated, parentPage, currentPage];
    }

    /**
     * Add a homepage link to the list of provided breadcrumb links.
     *
     * @param links the list of breadcrumb links
     */
    function addHomeLink(links: BreadcrumbType[]): LinkProps[] {
        return [
            {
                href: '/',
                templatePath: '/',
                children: t('Home'),
                size: 'small',
            },
            ...links,
        ] as LinkProps[];
    }

    return (
        <StyledBreadcrumb
            mobileVariant={mobileVariant}
            desktopVariant={desktopVariant}
            {...otherProps}
        >
            <WrappedLink
                prefetch={false}
                href={getBackTemplatePath(links)}
                as={getBackHref(links)}
                data-testid="breadcrumb__back-button"
                className="breadcrumb-mobile"
                prefix={<BackIcon />}
                variant={LinkVariant.CUSTOM}
            >
                {getBackText(links)}
            </WrappedLink>
            <LinkList
                links={addHomeLink(isTablet ? truncateLinks(links) : links)}
                ordered={true}
                navigation={true}
                separator={LinkListSeparator.SLASH}
                separatorSize={LinkSize.SMALL}
                data-testid="breadcrumb__link-list"
                className="breadcrumb-link-list"
            />
        </StyledBreadcrumb>
    );
};
